import React, {useEffect, useRef} from 'react';
import {DynamicWalletConnectWrapper} from '@stripe-internal/wallet-connect-wrapper';
import {setValue, useSelector} from 'src/v2/state/store';
import {WalletConnectionClientContext} from 'src/v2/state/walletConnection';
import {getInitialScreen} from 'src/v2/state/getInitialScreen';
import {useSessionContext} from 'src/state/usePayinSessionContext';
import {WalletRegistry} from 'src/v2/util/WalletRegistry';
import {logger} from 'src/utils/logger';

interface WalletConnectionClientProviderProps {
  children: React.ReactNode;
}

export const WalletConnectionClientProvider: React.FC<
  WalletConnectionClientProviderProps
> = ({children}) => {
  const walletClientRef = useRef<DynamicWalletConnectWrapper | null>(null);

  const {mode} = useSessionContext();
  const livemode = useSelector((state) => state.livemode);
  const disabledWalletProviders = useSelector(
    (state) => state.disabledWalletProviders,
  );
  const enableSmartContractWalletDetection = useSelector(
    (state) => state.featureFlags.enable_smart_contract_wallet_detection,
  );
  const supportedCurrencies = useSelector((state) => state.supportedCurrencies);

  useEffect(() => {
    if (walletClientRef.current) {
      return;
    }

    // Initialize DynamicWalletConnectWrapper using sessionModel data
    const wrapper = new DynamicWalletConnectWrapper({
      livemode,
      disabledWalletProviders: disabledWalletProviders || [],
      enableSmartContractWalletDetection,
    });

    // Set up callbacks to update v2 store
    wrapper.setOnActiveWallet((activeWallet) => {
      setValue((state) => {
        state.walletConnection.activeWallet = activeWallet;
      });
    });

    wrapper.setOnConnectedWallets((connectedWallets) => {
      setValue((state) => {
        state.walletConnection.connectedWallets = connectedWallets;
      });
    });

    wrapper.setOnIframeReady(
      ({activeWallet, walletOptions, exchangeOptions, device}) => {
        // Initialize wallet registry with the data
        WalletRegistry.init(walletOptions, exchangeOptions);

        setValue((state) => {
          state.walletConnection.activeWallet = activeWallet;
          state.walletConnection.device = device;

          const {screen, newWallet} = getInitialScreen({
            sessionState: state.state,
            activeWallet,
            mode,
            enablePayWithAnyCrypto:
              state.featureFlags.enable_pay_with_any_crypto,
          });

          state.screen = screen;
          if (newWallet) {
            state.newWallet = newWallet;
          }

          state.walletConnection.ready = true;

          logger.log('crypto_payin_session.wallet_connection.ready', {
            since_page_start: Math.round(performance.now()),
          });
        });
      },
    );

    wrapper.setOnWalletConnectQrCodeReady((uri) => {
      setValue((state) => {
        state.walletConnection.walletConnectQrCodeUri = uri;
      });
    });

    // Mount to DOM
    wrapper.mount('crypto-payins-wallet-connect');

    walletClientRef.current = wrapper;
  }, [
    livemode,
    disabledWalletProviders,
    enableSmartContractWalletDetection,
    mode,
    supportedCurrencies,
  ]);

  useEffect(() => {
    return () => {
      if (walletClientRef.current) {
        setValue((state) => {
          state.walletConnection.ready = false;
        });
        walletClientRef.current = null;
      }
    };
  }, []);

  const contextValue = {
    walletClientRef,
  };

  return (
    <WalletConnectionClientContext.Provider value={contextValue}>
      {children}
    </WalletConnectionClientContext.Provider>
  );
};
